import ReactApexChart from "react-apexcharts";
import { toTime } from "src/data/DateTimeUtil";
import { ApexOptions } from 'apexcharts';
import { Project } from "src/api/Project";
import { Country } from "src/api/Country";
import { useBreakpointValue } from "@chakra-ui/react";

const limitYear = (d: Date, startYear: number): Date => {
    const a = new Date(d);
    if (a.getUTCFullYear() < startYear) {
        a.setUTCFullYear(startYear, 0, 1);
        a.setUTCHours(0, 0, 0, 0);
    }
    return a;
}

const getFlagEmoji = (iso2: string) => {
    return iso2.toUpperCase().split('').map(char => String.fromCodePoint(127397 + char.charCodeAt(0))).join('');
};

const swissProjectRegion = (project: Project): string => {
    const name = project.name.toLowerCase();
    if (name.includes('talents')) return 'Asia';
    return name.includes('berufsbildung') || name.includes('grundschulbildung') ? 'Asia & Afrika' : 'Intern';
};

const projectCountryLabel = (project: Project, country: Country): string => {
    return country.iso2 === 'CH' ? swissProjectRegion(project) : getFlagEmoji(country.iso2) + ' ' + country.region + '/' + country.name;
};

// we have data starting from this year, regardless a few projects are dated from 2019
const minYear = 2020;

const ProjectTimeline = ({ projects, latestEndDate, countries, height }: { projects: Project[], countries: Country[], latestEndDate: Date, height: number }) => {
    // hide some information on smaller devices
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    const iso2Country = new Map(countries.map(c => [c.iso2, c]));
    const maxDate = new Date(new Date(latestEndDate).getFullYear() + 1, 0, 0, 0, 0, 0, 0);
    //console.log(`${minDate} - ${maxDate}`)
    const data = projects.map(p => {
        let startDate = limitYear(p.startDate, minYear);
        startDate.setUTCHours(0, 0, 0, 0);
        let point = {
            x: p.name.trim(),
            y: [
                toTime(startDate),
                toTime(p.endDate ?? maxDate),
            ],
            //fillColor: p.color // doesnt work with 1.7.0 colors are in the array 
        };
        return point;
    });
    const chartColors = projects.map(p => p.color);
    const series: NonNullable<ApexOptions["series"]> = [
        { data: data }
    ];
    let options: ApexOptions = {
        chart: {
            id: 'project-timeline',
            height: height,
            type: 'rangeBar',
            defaultLocale: 'en',
            toolbar: {
                show: true,
                tools: {
                    download: false,
                    selection: true,
                    zoom: false,
                    zoomin: true,
                    zoomout: true,
                    pan: false,
                    reset: false,
                },
            },
            zoom: {
                enabled: false,
            },
            dropShadow: {
                enabled: true,
                top: 2,
                left: 2,
                //blur: 2,
                //opacity: 0.8
            },
            animations: {
                enabled: false,
                //speed: 200,
                // animateGradually: {
                //     enabled: true,
                //     delay: 550
                // },
                // dynamicAnimation: {
                //     enabled: true,
                //     speed: 350
                // }
            },
        },
        plotOptions: {
            bar: {
                horizontal: true,
                distributed: true,
                dataLabels: {
                    hideOverflowingLabels: false
                }
            }
        },
        colors: chartColors,
        dataLabels: {
            enabled: true,
            // ApexCharts 7 enables label motion by default; keep the timeline static.
            animate: { enabled: false },
            // Preserve label placement from before ApexCharts 7.8.
            avoidOverlap: false,
            distributed: false,
            formatter: function (val, opts) {
                var label = opts.w.globals.labels[opts.dataPointIndex]
                // var a = formatOverview(val[0])
                // var b = formatOverview(val[1])
                // return `${label}: ${a} - ${b}`;
                return label;
            },
            style: {
                colors: ['#333', '#333'],
                fontWeight: 'bold',
            },
            background: {
                enabled: false,
            },
        },
        xaxis: {
            type: 'datetime',
            tickPlacement: 'on',
            axisTicks: {
                show: true // Show X-axis ticks
            },
        },
        yaxis: {
            show: (isSmallScreen ? false : true),
            labels: {
                minWidth: (isSmallScreen ? 0 : 126),
                style: {
                    colors: 'grey',
                },
                formatter: function (val: any, opts?: any) {
                    if (isSmallScreen) {
                        return '';
                    } else if (opts) {
                        let project = projects[opts.dataPointIndex]
                        let country = iso2Country.get(project?.country);
                        if (project && country) {
                            return projectCountryLabel(project, country);
                        } else {
                            return '';
                        }
                    } else {
                        return val;
                    }
                },
            },
        },
        grid: {
            borderColor: '#888',
            strokeDashArray: 7,
            position: 'front',
            xaxis: {
                lines: {
                    show: true,
                }
            },
            yaxis: {
                lines: {
                    show: false,
                }
            },
            row: {
                colors: ['white', 'grey'],
                opacity: 0.05
            },
        }
    };

    return (
        <div id="chart" >
            <ReactApexChart options={options} series={series} type="rangeBar" height={height} />
        </div >
    )
}

export default ProjectTimeline
